import { clearStoredToken, getStoredToken } from "../auth/AuthProvider";

import { API_BASE } from "./base";

// One reload per minute, not per 401: reloading lets AuthProvider re-exchange
// a stale token, but if the fresh token also 401s (clock skew, secret
// mismatch mid-deploy) an unguarded reload becomes an infinite loop hammering
// /auth/exchange and discarding all UI state.
const RELOAD_GUARD_KEY = "lodge_auth_reloaded_at";
const RELOAD_GUARD_WINDOW_MS = 60_000;

function reloadOncePerWindow() {
  const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
  if (Date.now() - lastReload > RELOAD_GUARD_WINDOW_MS) {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    window.location.reload();
  }
}

// Carries what the server actually said. The Lodge API answers 409 when a
// customer organisation is already linked and 400 when a field is wrong, both
// with a message meant for the person who typed it - and a bare Error would
// leave a form showing "Lodge API error: 409" instead of the reason.
export class LodgeApiError extends Error {
  readonly status: number;
  // The server's own message, trimmed. Empty when it sent none, in which case a
  // caller should fall back to its own wording rather than showing nothing.
  readonly detail: string;

  constructor(status: number, detail: string, message: string) {
    super(message);
    this.name = "LodgeApiError";
    this.status = status;
    this.detail = detail;
  }
}

export async function lodgeGet<T>(path: string): Promise<T> {
  const headers: HeadersInit = {};
  const token = getStoredToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers,
  });

  if (response.status === 401) {
    clearStoredToken();
    reloadOncePerWindow();
    throw new Error("Session expired");
  }

  if (!response.ok) {
    // A LodgeApiError rather than a plain one, so a caller can tell a record
    // that is gone from a read that failed. It extends Error, so anything
    // already catching this is unaffected.
    throw new LodgeApiError(
      response.status,
      "",
      `Lodge API error: ${response.status} GET ${path}`,
    );
  }
  return response.json();
}

export async function lodgeSend<T>(
  path: string,
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  body?: unknown,
): Promise<T> {
  const headers: HeadersInit = {};
  const token = getStoredToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (response.status === 401) {
    clearStoredToken();
    reloadOncePerWindow();
    throw new Error("Session expired");
  }

  if (!response.ok) {
    // Read the body before throwing: http.Error writes a plain-text reason and
    // it is the only thing that can tell a form which field to blame.
    //
    // Guarded because the detail is a bonus, never the point: a body that
    // cannot be read - a connection dropped mid-read, or a response object
    // without one - must still produce the status error rather than a
    // TypeError from inside the error path.
    let detail = "";
    try {
      detail = (await response.text()).trim();
    } catch {
      detail = "";
    }

    throw new LodgeApiError(
      response.status,
      detail,
      `Lodge API error: ${response.status} ${method} ${path}`,
    );
  }

  // A 204 has no body; parsing it would turn a successful DELETE into a
  // JSON error and make the caller roll back an operation that worked.
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
}
